import { z } from 'zod'

export const AutosendSettings = z.object({
  enabled: z.boolean(),
  firstMsgEnabled: z.boolean(),
  confirmMsgEnabled: z.boolean(),
})

export const UpdateAutosendBody = AutosendSettings.partial().refine(
  (v) => Object.keys(v).length > 0,
  { message: 'At least one field required' }
)

export const GetAutosendResponse = z.object({
  kv: z.boolean(),
  settings: AutosendSettings,
})

export type AutosendSettingsType = z.infer<typeof AutosendSettings>
export type UpdateAutosendInput = z.infer<typeof UpdateAutosendBody>
export type GetAutosendResult = z.infer<typeof GetAutosendResponse>

export const ServiceLogSeverity = z.enum(['info', 'warning', 'error'])
export const ServiceLogCategory = z.enum(['orders', 'cdek', 'messages', 'notifications', 'analytics', 'stockGuard', 'wildberries', 'system', 'unknown'])
export const ServiceLogSource = z.enum(['automatic', 'manual', 'system'])
// Mirrors forge.domain.service_events.ServiceEventReason exactly. A code the
// server can return but this list omits fails the page parse, which blanks the
// whole service log rather than one row.
export const ServiceLogReasonCode = z.enum([
 'automation_disabled', 'invalid_order_id', 'invalid_chat_id', 'invalid_message_id', 'missing_order_link',
 'cdek_link_not_yet_available', 'cdek_link_ambiguous', 'cdek_link_resolved', 'cdek_link_retry_exhausted',
 'missing_order_snapshot', 'not_dbs_order', 'order_not_processing', 'order_not_active', 'missing_customer_name',
 'order_cancelled', 'order_unpaid', 'order_already_completed',
 'incomplete_courier', 'dispatch_not_configured', 'chat_history_unavailable', 'chat_message_not_confirmation_code',
 'no_pending_courier_message', 'pending_courier_unavailable', 'ambiguous_pending_chat', 'pending_chat_join_mismatch',
 'not_cdek_handoff_event', 'not_delivery_courier_event', 'message_already_in_chat', 'already_sent',
 'already_claimed', 'waiting_for_code', 'provider_error', 'provider_data_unavailable', 'provider_rate_limited', 'sent_but_ack_failed',
 'duplicate_provider_event', 'invalid_webhook_secret', 'malformed_provider_payload', 'shadow_record_failed',
 'legacy_forward_failed', 'handoff_record_missing', 'webhook_subscription_mismatch',
 'webhook_subscription_wrong_endpoint', 'webhook_subscription_stale_entry', 'webhook_subscription_healthy',
 'webhook_subscription_registration_failed', 'candidate_capacity_exceeded', 'poll_cycle_completed',
 'unknown_order_status', 'wildberries_rate_limited', 'wildberries_retention_gap',
 'wildberries_operational_sales_failed', 'invalid_forecast_import', 'evidence_hash_mismatch',
 'order_amount_disagreement', 'callback_silence', 'callback_recovered', 'automation_stuck', 'automation_recovered',
 'claim_generation_conflict', 'claim_content_unverifiable', 'automation_disabled_during_resume',
 'claim_no_longer_carried', 'operator_marked_sent', 'operator_closed_unsent', 'operator_reopened_unsent',
 'report_unavailable', 'report_recovered', 'poller_fallback_bootstrap', 'poller_fallback_not_fresh',
 'poller_fallback_not_eligible', 'new_sale_not_a_sale', 'new_sale_observer_failed',
 'callback_work_pending', 'callback_provider_read_failed',
 'callback_retry_exhausted', 'callback_work_succeeded', 'notification_disabled', 'notification_event_disabled',
 'notification_event_not_configured', 'notification_not_configured', 'notification_provider_refused',
 'notification_provider_unavailable', 'notification_unexpected_outcome',
 'notification_retry_exhausted', 'callback_provider_refused',
 'callback_provider_response_invalid', 'callback_buyer_detail_unavailable', 'callback_automation_waiting',
 'order_observation_stale', 'order_generation_drift', 'order_persistence_failed',
 'orders_list_served_stale',
 'callback_synchronizer_unavailable',
 'order_campaign_mismatch',
 'notification_type_not_handled', 'notification_type_unknown',
 'scheduled_run_failed',
 'database_lock_timeout', 'database_statement_timeout', 'database_socket_deadline',
 'release_scheduler_commit_failed', 'release_observability_not_recreated',
 // a quarantine past its scheduled return, and a shipment paused by one
 'quarantine_overdue', 'shipment_quarantined', 'carrier_waybill_retired',
 // a carrier order that does not exist, or whose identity is unusable
 'carrier_order_not_established', 'cdek_order_uuid_not_recorded', 'cdek_order_uuid_ambiguous',
 // the Product identity a print must match exactly before the provider is asked
 'product_not_resolved', 'product_ambiguous', 'certificate_insufficient_facts',
 // what CDEK answered when a document was requested
 'document_not_ready', 'provider_not_configured', 'document_not_pdf', 'document_malformed',
 'barcode_page_count', 'barcode_geometry', 'order_page_count', 'order_geometry',
 // a subscribed event type that is registered and not arriving
 'webhook_type_silent', 'webhook_type_never_arrived',
 'webhook_subscription_repaired', 'stock_protection_identity_conflict',
 'product_attribution_applied',
 'economics_input_zero_coverage', 'economics_input_stale',
 'onec_syncer_heartbeat', 'onec_sync_run_completed', 'onec_sync_run_unchanged', 'onec_sync_run_failed',
])
export const ServiceLogItem = z.object({
  occurredAt: z.iso.datetime({ offset: true }),
  severity: ServiceLogSeverity,
  category: ServiceLogCategory,
  source: ServiceLogSource,
  summary: z.string().min(1).max(160),
  reasonCode: ServiceLogReasonCode.nullable().default(null),
}).strict()
export const ServiceLogPage = z.object({
  items: z.array(ServiceLogItem),
  nextCursor: z.string().min(1).max(512).nullable(),
  hasMore: z.boolean(),
}).strict()

export type ServiceLogSeverityType = z.infer<typeof ServiceLogSeverity>
export type ServiceLogCategoryType = z.infer<typeof ServiceLogCategory>
export type ServiceLogSourceType = z.infer<typeof ServiceLogSource>
export type ServiceLogReasonCodeType = z.infer<typeof ServiceLogReasonCode>
export type ServiceLogItemType = z.infer<typeof ServiceLogItem>
export type ServiceLogPageType = z.infer<typeof ServiceLogPage>

const ApiKeyTimestamp = z.iso.datetime({ offset: true })

export const ApiKeySchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(64),
  prefix: z.string().regex(/^[a-z2-7]{8}$/),
  createdAt: ApiKeyTimestamp,
  lastUsedAt: ApiKeyTimestamp.nullable(),
  revokedAt: ApiKeyTimestamp.nullable(),
  scope: z.enum(['owner', 'onec_sync']),
}).strict()

export const ApiKeyListResponseSchema = z.object({
  keys: z.array(ApiKeySchema),
}).strict()

export const ApiKeyScopeSchema = z.enum(['owner', 'onec_sync'])

export const ApiKeyCreateBodySchema = z.object({
  name: z.string().trim().min(1).max(64),
  scope: ApiKeyScopeSchema,
}).strict()

export const ApiKeyCreatedResponseSchema = z.object({
  key: ApiKeySchema,
  secret: z.string().regex(/^almaz_ak_[a-z2-7]{8}_[A-Za-z0-9_-]{43}$/),
}).strict()

export type ApiKey = z.infer<typeof ApiKeySchema>
export type ApiKeyScope = z.infer<typeof ApiKeyScopeSchema>
export type ApiKeyCreateBody = z.infer<typeof ApiKeyCreateBodySchema>
export type ApiKeyCreatedResponse = z.infer<typeof ApiKeyCreatedResponseSchema>
